import type { Action } from '@reduxjs/toolkit'
import type { DraftState } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { orderWizardActions as wizard } from '../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { SortedDrop } from './dropPipeline.ts'
import { poolChoices } from './pools.ts'
import { plural } from '../../Util/plural.ts'

/** How long a drop's confirmation stays before it goes by itself. */
export const DROP_NOTICE_MS = 8000

/** What one drop did, for the short confirmation after it. */
export type DropNotice = {
  draftId: string
  /** Distinguishes drops, so a new one restarts the confirmation. */
  id: string
  files: number
  readded: number
  rejected: number
  samples: number
  /** R2 files that joined samples missing theirs. */
  joined?: number
  /** Dropped files the draft already had, with nothing to do for them. */
  known: number
  /** Further copies of a file within the drop. */
  repeated?: number
  /** Files refused because the order holds a different file under the same name. */
  conflicts?: number
  /** The 1-based pool the new files went to, only when the order has several pools. */
  pool?: number
  sheet?: 'applied' | 'failed'
}

/** `undefined` for a drop that failed, whose error is shown instead, or that held nothing. */
export const noticeOfDrop = ({
  id,
  draftId,
  drop,
  actions,
  after,
  pool
}: {
  id: string
  draftId: string
  drop: SortedDrop
  /** The actions the drop's processing emitted. */
  actions: readonly Action[]
  after: DraftState | undefined
  pool?: number
}): DropNotice | undefined => {
  if (actions.some(wizard.detectionFailed.match) || drop.handles.length === 0) {
    return undefined
  }
  const completed = actions.find(wizard.detectionCompleted.match)?.payload
  const rejections = new Set(Object.keys(completed?.rejections ?? {}))
  const files = drop.toAnalyse.filter(({ key }) => !rejections.has(key)).length
  const unknownKind = drop.added.filter((file) => file.kind === 'other').length
  const sheet = actions.some(wizard.sheetLoaded.match) ? 'applied' : actions.some(wizard.sheetFailed.match) ? 'failed' : undefined
  const pools = after ? poolChoices(after) : []
  const target = pools.find((choice) => choice.pool === (pool ?? pools[0]?.pool))
  return {
    id,
    draftId,
    files,
    readded: drop.reattached.length,
    rejected: rejections.size + unknownKind,
    samples: completed?.groups.length ?? 0,
    ...(completed?.attachments?.length ? { joined: completed.attachments.length } : {}),
    known: drop.known,
    ...(drop.repeated > 0 ? { repeated: drop.repeated } : {}),
    ...(drop.conflicts.length > 0 ? { conflicts: drop.conflicts.length } : {}),
    ...(pools.length > 1 && files > 0 && target ? { pool: target.number } : {}),
    ...(sheet ? { sheet } : {})
  }
}

/** "3 files added to pool 2 · 1 new sample". */
export const dropNoticeText = (notice: DropNotice): string => {
  const parts = [
    notice.files > 0 && `${plural(notice.files, 'file')} added${notice.pool ? ` to pool ${notice.pool}` : ''}`,
    notice.readded > 0 && `${plural(notice.readded, 'file')} re-added`,
    notice.samples > 0 && plural(notice.samples, 'new sample'),
    !!notice.joined && `${plural(notice.joined, 'R2 file')} joined ${notice.joined === 1 ? 'its sample' : 'their samples'}`,
    notice.sheet === 'applied' && 'sample sheet read',
    notice.sheet === 'failed' && 'the sample sheet could not be read',
    notice.rejected > 0 && `${plural(notice.rejected, 'file')} can’t be used`,
    !!notice.conflicts && `${plural(notice.conflicts, 'file')} not added: a different file with the same name is already in this order`,
    notice.known > 0 && `${plural(notice.known, 'file')} ${notice.known === 1 ? 'was' : 'were'} already in this order — skipped`,
    !!notice.repeated && `${plural(notice.repeated, 'duplicate file')} skipped`
  ].filter((part): part is string => !!part)
  if (parts.length === 0) {
    return 'Nothing added'
  }
  const text = parts.join(' · ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}
